import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAdminAllowedRoles, hasRole } from '../lib/auth';

/**
 * Route gate. The two axes are enforced where they can be decided correctly:
 *
 *   requiredRole   → ORG axis (admin portal). admin/super_admin. Decided here —
 *                    org role is always known from /api/me.
 *   requiredAccess → PAGE SHELL visibility. Authenticated users may open the
 *                    page shell. PER-PROJECT authorization is NOT a route-level
 *                    decision: it depends on the active project's membership,
 *                    which isn't reliably known at route-eval time (it is fetched
 *                    async into ProjectContext, and org-level pages like the
 *                    projects list have no active project at all). Enforcing it
 *                    here wrongly bounced admins/members off /projects and
 *                    /dashboard. Instead, per-project access is enforced at the
 *                    two places that own the truth: the backend (404 = no
 *                    membership / 403 = insufficient level) and the project-aware
 *                    components via ProjectContext.can() (which disable/hide the
 *                    individual controls per the caller's project role).
 *
 * Ordering is invariant: loading → temp-password → auth → requiredRole.
 */
const ProtectedRoute = ({ children, requiredAccess, requiredRole }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-slate-900" />
          <p className="text-sm text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Force temp password change before accessing anything else.
  if (user?.is_temp_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Admin-portal routes: ORG axis only (admin / super_admin).
  if (requiredRole) {
    if (!hasRole(user?.role, getAdminAllowedRoles(requiredRole))) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // requiredAccess: page-shell visibility only (authenticated, checked above).
  // Per-project authorization is enforced by the backend (404/403) and by
  // ProjectContext.can() inside the project-aware components — not here.

  // children → wrapper mode; otherwise <Outlet /> → layout route mode.
  return children ?? <Outlet />;
};

export default ProtectedRoute;
